// src/components/ApprovalGateDemo.tsx
//
// Real recreation of ChatScreen.tsx's actual approval-gate mechanism —
// PendingApprovalCard + disabled textarea — using the real shadcn/
// Base UI primitives (Bubble, Marker, Button), not a styled
// approximation. Simulated interaction only: no real resolveApproval
// call, no backend round-trip. Labeled as a demo in the surrounding
// page copy for exactly that reason.

import * as React from "react"
import { useState } from "react"
import { Bubble, BubbleContent } from "./ui/bubble"
import { Marker, MarkerIcon, MarkerContent } from "./ui/marker"
import { Button } from "./ui/button"
import { InfoIcon, CheckIcon, XIcon } from "lucide-react"

type GateState = "pending" | "approved" | "declined"

export const ApprovalGateDemo: React.FC = () => {
  const [state, setState] = useState<GateState>("pending")

  return (
    <div className="max-w-md mx-auto rounded-2xl border border-brand-border bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-brand-border">
        <span className="text-sm font-medium text-brand-black">Chat</span>
        <span className="text-xs text-brand-gray">FDW</span>
      </div>

      <div className="p-4 flex flex-col gap-3">
        <Bubble align="end" variant="default">
          <BubbleContent>
            Reply to the vendor and confirm the updated terms.
          </BubbleContent>
        </Bubble>

        <Bubble align="start" variant="outline">
          <BubbleContent>
            Called <code>send_message</code>
          </BubbleContent>
        </Bubble>

        {state === "pending" && (
          <Marker variant="border" className="text-foreground">
            <MarkerIcon>
              <InfoIcon />
            </MarkerIcon>
            <MarkerContent className="w-full">
              <div className="space-y-3">
                <p>
                  Waiting on your approval for <code>send_message</code> before this continues.
                </p>
                <pre className="rounded-md bg-muted px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
{`{
  "to": "vendor@acme.com",
  "body": "Confirming the updated terms..."
}`}
                </pre>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => setState("approved")}
                    className="flex items-center gap-1.5"
                  >
                    <CheckIcon className="size-4" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setState("declined")}
                    className="flex items-center gap-1.5"
                  >
                    <XIcon className="size-4" />
                    Decline
                  </Button>
                </div>
              </div>
            </MarkerContent>
          </Marker>
        )}

        {state !== "pending" && (
          <Marker>
            <MarkerContent className="text-xs">
              {state === "approved" ? "Message sent." : "Declined by human."}
            </MarkerContent>
          </Marker>
        )}
      </div>

      <div className="flex items-end gap-2 border-t border-border p-3">
        <textarea
          placeholder="Message FDW..."
          rows={1}
          disabled={state === "pending"}
          className="max-h-40 min-h-9 flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
        />
        <Button size="sm" disabled={state === "pending"}>
          Send
        </Button>
      </div>

      <p className="text-center text-xs text-brand-gray px-4 pb-4">
        {state === "pending"
          ? "The input is disabled — not as a rule, as a fact you can watch happen."
          : state === "approved"
            ? "It only sent because a human clicked approve."
            : "It never sent — the human said no."}
      </p>
    </div>
  )
}